<template>
  <AuthLayout>
    <AppTitle :title="tt('infrastructure.title.signIn')" />
    <InformationPanel v-if="isInformationPanelEnabled" />
    <main class="sign-in-view">
      <div class="sign-in-view__card">
        <div v-if="!isInformationPanelEnabled" class="sign-in-view__logo">
          <AppLogo />
        </div>
        <div class="sign-in-view__title">
          {{ tt('infrastructure.title.signIn') }}
        </div>
        <RadioGroup
          class="sign-in-view__account-types"
          :value="selectedAccountType"
          :optionType="ERadioButtonOptionType.radio"
          :layout="ELayout.horizontal"
          @change="handleAccountTypeChange"
        >
          <Radio :value="EAccountType.personal">
            {{ tt('shared.personal') }}
          </Radio>
          <Radio :value="EAccountType.corporate">
            {{ tt('shared.corporate') }}
          </Radio>
        </RadioGroup>
        <Form
          ref="formRef"
          :layout="ELayout.vertical"
          :rules="formRules"
          :model="formData"
          autocomplete="off"
          @finish="handleFinish"
          @validate="onValidateField"
        >
          <FormItem
            class="sign-in-view__form-item"
            :label="tt('shared.email')"
            name="email"
          >
            <Input
              v-model:value="formData.email"
              :placeholder="tt('infrastructure.form.placeholder.emailSignIn')"
              :size="ESize.large"
              :isInvalid="!formValidation[ESignInFields.email]"
              @input="handleControlChange(ESignInFields.email)"
              @blur="handleEmailChange"
            />
          </FormItem>

          <div class="sign-in-view__passwords-wrapper">
            <FormItem
              class="sign-in-view__form-item sign-in-view__password"
              :label="tt('shared.password')"
              name="password"
            >
              <PasswordInput
                v-model:value="formData.password"
                :size="ESize.large"
                :isInvalid="!formValidation[ESignInFields.password]"
                @input="handleControlChange(ESignInFields.password)"
              />
              <span v-if="isCommonError" class="sign-in-view__common-error">
                {{ commonErrorText }}
              </span>
            </FormItem>
            <FormItem
              v-if="isCorporateAccount"
              class="sign-in-view__form-item sign-in-view__company-code"
              :label="tt('infrastructure.form.label.companyCode')"
              name="companyCode"
            >
              <Select
                v-model:value="formData.companyCode"
                :options="companyCodes"
                :size="ESize.large"
                :dropdownMatchSelectWidth="180"
                :isInvalid="!formValidation[ESignInFields.companyCode]"
                @select="handleControlChange(ESignInFields.companyCode)"
              />
            </FormItem>
          </div>

          <div class="sign-in-view__buttons">
            <Button
              :htmlType="EHtmlType.submit"
              :loading="isLoading"
              :title="tt('infrastructure.action.signIn')"
              :size="ESize.large"
              :type="EButtonType.primary"
            />
            <SmartLink href="/forgot_password">
              {{ tt('infrastructure.action.forgotPassword') }}
            </SmartLink>
          </div>
        </Form>
        <LineDivider v-if="isSberButtonVisible" :text="tt('shared.or')" />
        <SberBusinessIntegrationButton
          v-if="isSberButtonVisible"
          class="analytics--sber-business-id-button-login"
          :integrationEvent="EIntegrationEvent.authorization"
          :size="ESberButtonSize.large"
          @completeAuth="handleCompleteSocialAuth"
          @completeRegistration="handleCompleteSocialRegistration"
        />

        <div
          class="sign-in-view__link"
          :class="{ 'sign-in-view__link_centered': !isInformationPanelEnabled }"
        >
          {{ tt('infrastructure.action.dontHaveAnAccount') }}
          <SmartLink href="/sign_up">
            {{ tt('infrastructure.action.goToSignUp') }}
          </SmartLink>
        </div>
        <SupportBlock v-if="!isInformationPanelEnabled" :theme="EThemeSupportBlock.light" />
      </div>
    </main>
  </AuthLayout>
</template>

<script lang="ts">
import {
  Form,
  FormItem,
} from 'ant-design-vue';
import {
  computed,
  defineComponent,
  reactive,
  ref,
  watch,
} from 'vue';

import {
  Button,
  Radio,
  RadioGroup,
  Input,
  PasswordInput,
  Select,
} from '@/ui';
import {
  EButtonType,
  ESize,
  EHtmlType,
  ERadioButtonOptionType,
} from '@/ui/types';
import { showErrorNotification } from '@/utils';
import useStore from '@/store/useStore';
import { useFormHandlers } from '@/composables/useFormHandlers';
import useViewState from '@/composables/useViewState';
import AuthLayout from '@/components/layouts/AuthLayout/index.vue';
import router from '@/router';
import tt from '@/i18n/utils/translateText';
import {
  DEFAULT_EMAIL_RULE,
  EAccountType,
  ELayout,
} from '@/constants';
import SberBusinessIntegrationButton from '@/components/SberBusinessIntegrationButton/index.vue';
import { useSberButtonVisible } from '@/components/SberBusinessIntegrationButton/composables/useSberButtonVisible';
import { EIntegrationEvent, ESberButtonSize } from '@/components/SberBusinessIntegrationButton/domain/constants';
import { TSessionWithTokensSerialized } from '@/contexts/accountingContext/domain/types';
import useAppTheme from '@/composables/application/useAppTheme';
import { SmartLink } from '@/ui/experimental';
import LineDivider from '@/components/LineDivider/index.vue';
import InformationPanel from '@/components/InformationPanel/index.vue';
import AppLogo from '@/components/AppLogo/index.vue';
import SupportBlock from '@/components/InformationPanel/components/SupportBlock/index.vue';
import { EThemeSupportBlock } from '@/components/InformationPanel/components/SupportBlock/constants';
import { httpStatus } from '@/api/constants';
import { useUserStore } from '@/stores/user';
import { TDomainError } from '@/types';
import logger from '@/logger';

import { useFetchData } from '../composables/useFetchData';
import {
  ESignInFields,
  TSignInPayload,
} from './types';

export default defineComponent({
  name: 'SignInView',
  components: {
    AppLogo,
    Button,
    LineDivider,
    SmartLink,
    AuthLayout,
    RadioGroup,
    Radio,
    Form,
    FormItem,
    Input,
    Select,
    PasswordInput,
    SberBusinessIntegrationButton,
    InformationPanel,
    SupportBlock,
  },
  setup() {
    const { getThemeConfig } = useAppTheme();
    const { isInformationPanelEnabled } = getThemeConfig();

    const store = useStore();
    const userStore = useUserStore();

    const formData = reactive({
      email: '',
      password: '',
      companyCode: '',
    });

    const rules = {
      email: [{
        required: true,
        message: tt('shared.rule.emailRequired'),
      },
      DEFAULT_EMAIL_RULE,
      ],
      password: [{
        required: true,
        message: tt('shared.rule.passwordRequired'),
      },
      {
        min: 6,
        message: tt('infrastructure.form.rule.passwordMin6'),
      },
      ],
      companyCode: [{
        required: true,
        message: tt('infrastructure.form.rule.companyCodeRequired'),
      },
      ],
    };

    const formRef = ref(null);
    const selectedAccountType = ref<EAccountType>(EAccountType.personal);
    const { isLoading, setViewStateAs } = useViewState();
    const { isSberButtonVisible } = useSberButtonVisible(EIntegrationEvent.authorization);
    const { companyCodes, fetchCompanyCode } = useFetchData();

    const {
      onControlChange,
      onValidateField,
      onError,
      clearValidation,
      formRules,
      commonError,
      formValidation,
    } = useFormHandlers(formRef, {
      rules,
      formData,
    });

    const isCorporateAccount = computed(() => selectedAccountType.value === EAccountType.corporate);
    const commonErrorText = computed(() => (!isCorporateAccount.value
      ? tt('infrastructure.error.wrongEmailOrPassword')
      : tt('infrastructure.error.wrongEmailOrPasswordOrCode')));

    const handleFinish = () => {
      const {
        email,
        password,
        companyCode,
      } = formData;

      const payload: TSignInPayload = {
        email,
        password,
      };

      if (isCorporateAccount.value) {
        payload.companyCode = companyCode;
      }

      setViewStateAs.loading();
      userStore.signIn(payload, store)
        .then(() => {
          setViewStateAs.done();
          // нам не нужно возвращать пользователя на страницу логина, если он ткнет кнопку "Назад" в браузере, поэтому replace
          router.replace('/');
        })
        .catch((error: TDomainError) => {
          setViewStateAs.withError();
          onError(error);

          if (error?.status >= httpStatus.INTERNAL_SERVER_ERROR.code) {
            showErrorNotification(error);
          }
          logger.error(error);
        });
    };

    const handleCompleteSocialAuth = (payload: TSessionWithTokensSerialized) => {
      setViewStateAs.loading();
      userStore.signInBySocial(payload, store)
        .then(setViewStateAs.done)
        .catch((error: TDomainError) => {
          setViewStateAs.withError();
          onError(error);

          if (error?.status >= httpStatus.INTERNAL_SERVER_ERROR.code) {
            showErrorNotification(error);
          }
          logger.error(error);
        });
    };

    const handleCompleteSocialRegistration = () => {
      router.push({
        path: 'sign_up',
        query: { socialAuth: 'sber' },
      });
    };

    const handleControlChange = (name: ESignInFields) => {
      onControlChange({ name });
    };

    const handleEmailChange = () => {
      if (formValidation[ESignInFields.email]) {
        if (isCorporateAccount.value) {
          formData.companyCode = '';
          companyCodes.value = [];
          fetchCompanyCode(formData.email);
        }
      }
    };

    const handleAccountTypeChange = (event: Event) => {
      if (!event.target) return;
      selectedAccountType.value = (event.target as HTMLInputElement).value as EAccountType;
      clearValidation();
    };

    watch(() => companyCodes.value, () => {
      if (companyCodes.value.length) {
        formData.companyCode = companyCodes.value[0].value as string;
        onControlChange({ name: ESignInFields.companyCode });
      }
    });

    return {
      formData,
      formRef,
      formRules,
      formValidation,
      companyCodes,

      isLoading,
      isCommonError: commonError,
      isCorporateAccount,
      isInformationPanelEnabled,
      isSberButtonVisible,
      selectedAccountType,
      commonErrorText,
      ELayout,
      EAccountType,
      EButtonType,
      EIntegrationEvent,
      ESberButtonSize,
      ESize,
      EHtmlType,
      ESignInFields,
      EThemeSupportBlock,
      ERadioButtonOptionType,

      onValidateField,
      handleFinish,
      handleControlChange,
      handleAccountTypeChange,
      handleCompleteSocialAuth,
      handleCompleteSocialRegistration,
      handleEmailChange,
      tt,
    };
  },
});
</script>

<style lang="scss" src="./styles.scss" />
